import React, { useCallback, useEffect, useRef, useState } from "react";
import { applicationsApi } from "../services/api";
import { getStorage } from "./storageHelper";
import { customerInfoIdValue } from "./helperConstants";
import {
  CameraCaptureProps,
  CaptureStep,
  DLScanModalProps,
  DlScanResult,
  ScanPhase,
} from "./types";

// Portrait and landscape overlays — single source of truth
const OVERLAY_PORTRAIT = {
  top: 0.18,
  left: 0.08,
  width: 0.84,
  height: 0.3,
} as const;
const OVERLAY_LANDSCAPE = {
  top: 0.12,
  left: 0.2,
  width: 0.6,
  height: 0.7,
} as const;

const CORNER_PX = 22;
const CIRC = 251.2;

// ─── Camera constraints ───────────────────────────────────────────────────────
const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: { ideal: "environment" },
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  // @ts-ignore — advanced constraints: iOS 15.4+ / Chrome Android
  advanced: [{ focusMode: "continuous" }],
};

// ─── Tap-to-focus ─────────────────────────────────────────────────────────────
async function tapToFocus(track: MediaStreamTrack, x: number, y: number) {
  try {
    const caps = (track as any).getCapabilities?.();
    if (!caps?.focusMode?.includes("manual")) return;
    await (track as any).applyConstraints({
      advanced: [
        { focusMode: "manual", focusDistance: caps.focusDistance?.min ?? 0 },
      ],
    });
    await (track as any).applyConstraints({
      advanced: [{ pointsOfInterest: [{ x, y }], focusMode: "continuous" }],
    });
  } catch {
    /* silently ignore — unsupported on desktop / older browsers */
  }
}

function cropToOverlay(
  video: HTMLVideoElement,
  overlayPortrait: typeof OVERLAY_PORTRAIT,
  overlayLandscape: typeof OVERLAY_LANDSCAPE,
): Promise<Blob | null> {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  const rect = video.getBoundingClientRect();

  // Decide overlay purely from live rect — no hook, no state, no stale values
  const overlay = rect.width > rect.height ? overlayLandscape : overlayPortrait;

  const cAR = rect.width / rect.height;
  const vAR = vw / vh;

  let renderW: number, renderH: number, offsetX: number, offsetY: number;
  if (vAR > cAR) {
    renderH = vh;
    renderW = vh * cAR;
    offsetX = (vw - renderW) / 2;
    offsetY = 0;
  } else {
    renderW = vw;
    renderH = vw / cAR;
    offsetX = 0;
    offsetY = (vh - renderH) / 2;
  }

  const sx = Math.max(0, offsetX + overlay.left * renderW);
  const sy = Math.max(0, offsetY + overlay.top * renderH);
  const sw = Math.min(overlay.width * renderW, vw - sx);
  const sh = Math.min(overlay.height * renderH, vh - sy);

  const needsRotation = sh > sw;

  const canvas = document.createElement("canvas");
  canvas.width = needsRotation ? sh : sw;
  canvas.height = needsRotation ? sw : sh;

  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;

  if (needsRotation) {
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((90 * Math.PI) / 180);
    ctx.drawImage(video, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh);
  } else {
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
  }

  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.95));
}

// ─── Merge front and back into a single side-by-side JPEG ────────────────────
async function mergeFrontBack(front: Blob, back: Blob): Promise<Blob> {
  const [bmpF, bmpB] = await Promise.all([
    createImageBitmap(front),
    createImageBitmap(back),
  ]);
  const h = Math.max(bmpF.height, bmpB.height);
  const scF = h / bmpF.height;
  const scB = h / bmpB.height;
  const wF = Math.round(bmpF.width * scF);
  const wB = Math.round(bmpB.width * scB);
  const gap = 24;

  const canvas = document.createElement("canvas");
  canvas.width = wF + gap + wB;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#111";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(bmpF, 0, 0, wF, h);
  ctx.drawImage(bmpB, wF + gap, 0, wB, h);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob failed"))),
      "image/jpeg",
      0.92,
    ),
  );
}

function CameraCapture({
  step,
  phase,
  frontBlob,
  decodeError,
  onCaptured,
  onPreviewContinue,
  onRetryBack,
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const trackRef = useRef<MediaStreamTrack | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const isCounting = countdown !== null;
  const isLandscape = useIsLandscape();
  const OVERLAY = isLandscape ? OVERLAY_LANDSCAPE : OVERLAY_PORTRAIT;

  const [streamKey, setStreamKey] = useState(0);

  useEffect(() => {
    // When orientation changes, restart the stream after a short delay
    // (delay lets the browser finish rotating before requesting new dimensions)
    const timer = setTimeout(() => {
      setStreamKey((prev) => prev + 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [isLandscape]);

  // Start/stop camera stream whenever we enter capturing phase
  useEffect(() => {
    if (phase !== "capturing") return;
    let mounted = true;

    (async () => {
      try {
        // Stop any existing stream first
        streamRef.current?.getTracks().forEach((t) => t.stop());

        const stream = await navigator.mediaDevices.getUserMedia({
          video: VIDEO_CONSTRAINTS,
          audio: false,
        });
        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        trackRef.current = stream.getVideoTracks()[0];
        try {
          await (trackRef.current as any).applyConstraints({
            advanced: [{ focusMode: "continuous" }],
          });
        } catch {}
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e: any) {
        if (mounted) setCamError(e?.message ?? "Camera unavailable");
      }
    })();

    return () => {
      mounted = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [phase, streamKey]);

  const handleVideoTap = useCallback(
    (e: React.PointerEvent<HTMLVideoElement>) => {
      if (!trackRef.current) return;
      const r = e.currentTarget.getBoundingClientRect();
      tapToFocus(
        trackRef.current,
        (e.clientX - r.left) / r.width,
        (e.clientY - r.top) / r.height,
      );
    },
    [],
  );

  const handleCapture = useCallback(async () => {
    if (!videoRef.current || isCounting) return;
    for (let i = 1; i >= 1; i--) {
      setCountdown(i);
      await new Promise((r) => setTimeout(r, 1000));
    }
    setCountdown(null);
    const blob = await cropToOverlay(
      videoRef.current,
      OVERLAY_PORTRAIT,
      OVERLAY_LANDSCAPE,
    );
    if (blob) onCaptured(blob);
  }, [isCounting, onCaptured]);

  // ── Status screens ─────────────────────────────────────────────────────────
  if (camError) {
    return (
      <div style={s.statusScreen}>
        <div style={{ fontSize: 40 }}>📷</div>
        <div style={s.statusTitle}>{camError}</div>
        <div style={s.statusBody}>Check camera permissions in Settings.</div>
      </div>
    );
  }

  if (phase === "decoding" || phase === "merging" || phase === "uploading") {
    const label =
      phase === "decoding"
        ? "Reading barcode…"
        : phase === "merging"
          ? "Preparing image…"
          : "Uploading…";
    return (
      <div style={s.statusScreen}>
        <div style={s.spinner} />
        <div style={s.statusTitle}>{label}</div>
        {phase === "decoding" && (
          <div style={s.statusBody}>
            Scanning PDF417 barcode on back of license
          </div>
        )}
      </div>
    );
  }

  if (phase === "decode_error") {
    return (
      <div style={s.statusScreen}>
        <div style={{ fontSize: 44 }}>⚠️</div>
        <div style={s.statusTitle}>Barcode not detected</div>
        <div style={s.statusBody}>
          The back of the license could not be read. Make sure the barcode is
          fully inside the frame, well-lit, and not covered.
        </div>
        {/* {decodeError && <div style={s.statusDetail}>{decodeError}</div>} */}
        <button onClick={onRetryBack} style={s.retryBtn}>
          Retake back of license
        </button>
      </div>
    );
  }

  if (phase === "preview") {
    return <PreviewScreen blob={frontBlob} onContinue={onPreviewContinue} />;
  }

  function PreviewScreen({
    blob,
    onContinue,
  }: {
    blob: Blob | null;
    onContinue: () => void;
  }) {
    const [url, setUrl] = useState<string>("");

    useEffect(() => {
      if (!blob) return;
      const objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }, [blob]);

    return (
      <div style={s.statusScreen}>
        <div style={s.previewBadge}>Front captured ✓</div>
        {url ? (
          <img
            src={url}
            alt="Front of license"
            style={{
              width: "auto",
              maxWidth: 300,
              height: "auto",
              maxHeight: "35vh",
              borderRadius: 8,
              border: "2px solid #00e070",
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : (
          <div style={{ color: "#555", fontSize: 13 }}>Loading preview…</div>
        )}
        <div style={s.statusBody}>
          Now flip the card and capture the{" "}
          <strong style={{ color: "#fff" }}>back side</strong>.
        </div>
        <button onClick={onContinue} style={s.continueBtn}>
          Continue to back →
        </button>
      </div>
    );
  }

  // ── Live camera ────────────────────────────────────────────────────────────
  const O = OVERLAY;
  const ringOffset = isCounting ? CIRC * (1 - countdown! / 3) : CIRC;

  return (
    <div style={s.cameraShell}>
      <video
        ref={videoRef}
        playsInline
        muted
        style={s.video}
        onPointerDown={handleVideoTap}
      />

      {/* Dark masks outside the guide box */}
      <div
        style={{
          ...s.mask,
          top: 0,
          left: 0,
          width: "100%",
          height: `${O.top * 100}%`,
        }}
      />
      <div
        style={{
          ...s.mask,
          top: `${(O.top + O.height) * 100}%`,
          left: 0,
          width: "100%",
          bottom: 0,
        }}
      />
      <div
        style={{
          ...s.mask,
          top: `${O.top * 100}%`,
          left: 0,
          width: `${O.left * 100}%`,
          height: `${O.height * 100}%`,
        }}
      />
      <div
        style={{
          ...s.mask,
          top: `${O.top * 100}%`,
          right: 0,
          width: `${(1 - O.left - O.width) * 100}%`,
          height: `${O.height * 100}%`,
        }}
      />

      {/* Corner brackets — bottom two use translate(0,-100%) to point inward/upward */}
      <div
        style={{
          ...s.corner,
          top: `${O.top * 100}%`,
          left: `${O.left * 100}%`,
          borderTop: "3px solid #00e070",
          borderLeft: "3px solid #00e070",
        }}
      />
      <div
        style={{
          ...s.corner,
          top: `${O.top * 100}%`,
          right: `${(1 - O.left - O.width) * 100}%`,
          borderTop: "3px solid #00e070",
          borderRight: "3px solid #00e070",
        }}
      />
      <div
        style={{
          ...s.corner,
          top: `${(O.top + O.height) * 100}%`,
          left: `${O.left * 100}%`,
          transform: "translate(0,-100%)",
          borderBottom: "3px solid #00e070",
          borderLeft: "3px solid #00e070",
        }}
      />
      <div
        style={{
          ...s.corner,
          top: `${(O.top + O.height) * 100}%`,
          right: `${(1 - O.left - O.width) * 100}%`,
          transform: "translate(0,-100%)",
          borderBottom: "3px solid #00e070",
          borderRight: "3px solid #00e070",
        }}
      />

      {/* Step pill */}
      <div
        style={{
          position: "absolute",
          top: `${(OVERLAY.top - (isLandscape ? 0.09 : 0.075)) * 100}%`,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,224,112,0.12)",
          border: "1px solid rgba(0,224,112,0.6)",
          color: "#00e070",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.07em",
          padding: "4px 14px",
          borderRadius: 20,
          whiteSpace: "nowrap",
          pointerEvents: "none",
          zIndex: 10,
        }}
      >
        {step === "front" ? "Scan front of license" : "Scan back of license"}
      </div>

      {/* Hint */}
      <div
        style={{
          position: "absolute",
          top: `${(OVERLAY.top + OVERLAY.height + (isLandscape ? 0.015 : 0.025)) * 100}%`,
          left: "50%",
          transform: "translateX(-50%)",
          color: "#fff",
          fontSize: 12,
          whiteSpace: "nowrap",
          opacity: 0.65,
          pointerEvents: "none",
          letterSpacing: "0.03em",
        }}
      >
        {isCounting ? "Hold steady" : "Align license · tap to focus"}
      </div>

      {/* Shutter + countdown ring */}
      <div
        style={{
          position: "absolute",
          ...(isLandscape
            ? { right: 16, top: "50%", transform: "translateY(-50%)" } // right side, vertically centred
            : { bottom: 20, left: "50%", transform: "translateX(-50%)" }), // bottom centre (portrait)
          width: 88,
          height: 88,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10,
        }}
      >
        {isCounting && (
          <svg
            width="88"
            height="88"
            viewBox="0 0 88 88"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
            }}
          >
            <circle
              cx="44"
              cy="44"
              r="40"
              fill="none"
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="4"
            />
            <circle
              cx="44"
              cy="44"
              r="40"
              fill="none"
              stroke="#00e070"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={CIRC}
              strokeDashoffset={ringOffset}
              style={{
                transform: "rotate(-90deg)",
                transformOrigin: "44px 44px",
                transition: "stroke-dashoffset 0.9s linear",
              }}
            />
          </svg>
        )}
        {isCounting && <div style={s.countdownNum}>{countdown}</div>}
        <button
          onClick={handleCapture}
          disabled={isCounting}
          style={s.shutterBtn}
          aria-label={`Capture ${step} of license`}
        >
          <div style={{ ...s.shutterOuter, opacity: isCounting ? 0.3 : 1 }} />
          <div style={{ ...s.shutterInner, opacity: isCounting ? 0.3 : 1 }} />
        </button>
      </div>
    </div>
  );
}

function useIsLandscape() {
  const [landscape, setLandscape] = useState(
    () => window.innerWidth > window.innerHeight,
  );
  useEffect(() => {
    const handler = () => setLandscape(window.innerWidth > window.innerHeight);
    window.addEventListener("resize", handler);
    window.addEventListener("orientationchange", handler);
    window.screen.orientation?.addEventListener("change", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("orientationchange", handler);
      window.screen.orientation?.removeEventListener("change", handler);
    };
  }, []);
  return landscape;
}

// ─── DLScanModal (exported) ───────────────────────────────────────────────────

export function DLScanModal({
  open,
  onClose,
  onCapture,
  onScanned,
  isDLMode = false,
}: DLScanModalProps) {
  const [step, setStep] = useState<CaptureStep>("front");
  const [phase, setPhase] = useState<ScanPhase>("capturing");
  const [frontBlob, setFrontBlob] = useState<Blob | null>(null);
  const [decodeError, setDecodeError] = useState<string | null>(null);
  const infoId = getStorage(customerInfoIdValue);
  const hasRetriedScan = useRef(false);

  // Reset every time the modal opens
  useEffect(() => {
    if (open) {
      setStep("front");
      setPhase("capturing");
      setFrontBlob(null);
      setDecodeError(null);
    }
  }, [open]);

  const handleCaptured = useCallback(
    async (blob: Blob) => {
      // ── Non-DL: single capture → upload ──────────────────────────────────────
      if (!isDLMode) {
        setPhase("uploading");
        await onCapture(
          new File([blob], "Document.jpg", { type: "image/jpeg" }),
        );
        return;
      }

      // ── DL front ─────────────────────────────────────────────────────────────
      if (step === "front") {
        setFrontBlob(blob);
        setStep("back");
        setPhase("preview");
        return;
      }

      // ── DL back: decode → merge → upload ─────────────────────────────────────
      setPhase("decoding");
      try {
        const back = new File([blob], "DriverLicense.jpg", {
          type: "image/jpeg",
        });
        const scanResponse = await applicationsApi.scanDL(infoId || "", back);
        if (!scanResponse?.success) {
          if (!hasRetriedScan.current) {
            hasRetriedScan.current = true;
            setPhase("decode_error");
            return;
          }
        }
      } catch (err: any) {
        if (!hasRetriedScan.current) {
          hasRetriedScan.current = true;
          setDecodeError(err?.message ?? "No PDF417 barcode found");
          setPhase("decode_error");
          return;
        }
      }

      setPhase("merging");
      let merged: Blob;
      try {
        merged = await mergeFrontBack(frontBlob!, blob);
      } catch (err: any) {
        setDecodeError("Failed to compose final image");
        setPhase("decode_error");
        return;
      }

      setPhase("uploading");
      await onCapture(
        new File([merged], "license.jpg", { type: "image/jpeg" }),
      );
    },
    [isDLMode, step, frontBlob, onCapture, onScanned],
  );

  const handlePreviewContinue = useCallback(() => setPhase("capturing"), []);
  const handleRetryBack = useCallback(() => {
    setDecodeError(null);
    setPhase("capturing");
  }, []);

  if (!open) return null;

  const headerLabel = !isDLMode
    ? "Scan Document"
    : step === "front"
      ? "Scan front of license"
      : phase === "preview"
        ? "Front captured"
        : "Scan back of license";

  return (
    <div style={s.backdrop} onClick={(e) => e.stopPropagation()}>
      <div style={s.modal} className="dlscan-modal">
        {/* Header */}
        <div style={s.header}>
          {isDLMode ? (
            <div style={s.progressWrap}>
              <div style={{ ...s.dot, background: "#00e070" }} />
              <div
                style={{
                  width: 28,
                  height: 2,
                  borderRadius: 1,
                  transition: "background 0.4s",
                  background:
                    step === "back" && phase !== "preview" ? "#00e070" : "#ddd",
                }}
              />
              <div
                style={{
                  ...s.dot,
                  transition: "background 0.4s",
                  background:
                    step === "back" && phase !== "preview" ? "#00e070" : "#ddd",
                }}
              />
              <span style={s.progressLabel}>{headerLabel}</span>
            </div>
          ) : (
            <strong style={{ fontSize: 15 }}>{headerLabel}</strong>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={s.closeBtn}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <CameraCapture
          step={step}
          phase={phase}
          frontBlob={frontBlob}
          decodeError={decodeError}
          onCaptured={handleCaptured}
          onPreviewContinue={handlePreviewContinue}
          onRetryBack={handleRetryBack}
        />
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    minHeight: "-webkit-fill-available",
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    marginTop: 0,
  },
  modal: {
    width: "min(100vw, 720px)",
    height: "min(100vh, min(100svh, min(100dvh, 900px)))",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    padding: "10px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: "1px solid #eee",
    flexShrink: 0,
    minHeight: 52,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    cursor: "pointer",
    padding: "4px 8px",
    color: "#444",
  },
  progressWrap: { display: "flex", alignItems: "center", gap: 6 },
  dot: { width: 10, height: 10, borderRadius: "50%" },
  progressLabel: {
    marginLeft: 10,
    fontSize: 14,
    fontWeight: 600,
    color: "#333",
  },

  cameraShell: {
    position: "relative",
    flex: 1,
    background: "#000",
    overflow: "hidden",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  mask: {
    position: "absolute",
    background: "rgba(0,0,0,0.55)",
    pointerEvents: "none",
  },
  corner: {
    position: "absolute",
    width: CORNER_PX,
    height: CORNER_PX,
    pointerEvents: "none",
    zIndex: 10,
    boxSizing: "border-box",
  },
  countdownNum: {
    position: "absolute",
    fontSize: 28,
    fontWeight: 600,
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
    zIndex: 2,
  },
  shutterBtn: {
    position: "relative",
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
  },
  shutterOuter: {
    position: "absolute",
    width: 72,
    height: 72,
    borderRadius: "50%",
    border: "3px solid #fff",
    boxSizing: "border-box",
    transition: "opacity 0.2s",
    pointerEvents: "none",
  },
  shutterInner: {
    position: "absolute",
    width: 54,
    height: 54,
    borderRadius: "50%",
    background: "#fff",
    transition: "transform 0.1s ease, opacity 0.2s",
    pointerEvents: "none",
  },
  statusScreen: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "#0d0d0d",
    gap: 16,
    padding: 32,
    textAlign: "center",
    overflowY: "auto",
    minHeight: 0,
  },
  statusTitle: { fontSize: 20, fontWeight: 600, color: "#fff" },
  statusBody: { fontSize: 14, color: "#999", maxWidth: 280, lineHeight: 1.65 },
  statusDetail: {
    fontSize: 11,
    color: "#555",
    fontFamily: "monospace",
    maxWidth: 280,
    wordBreak: "break-all",
  },
  retryBtn: {
    marginTop: 8,
    padding: "12px 28px",
    background: "#00e070",
    color: "#000",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },

  previewBadge: {
    fontSize: 13,
    fontWeight: 700,
    color: "#00e070",
    letterSpacing: "0.06em",
  },
  continueBtn: {
    marginTop: 8,
    padding: "13px 32px",
    background: "#fff",
    color: "#000",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
  },

  errorBox: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
    padding: 24,
    color: "#666",
    textAlign: "center",
    background: "#0d0d0d",
  },
  spinner: {
    width: 48,
    height: 48,
    border: "4px solid rgba(255,255,255,0.1)",
    borderTop: "4px solid #00e070",
    borderRadius: "50%",
    animation: "dlspin 0.8s linear infinite",
  },
};

// Inject keyframe once
if (typeof document !== "undefined" && !document.getElementById("__dlspin__")) {
  const st = document.createElement("style");
  st.id = "__dlspin__";
  st.textContent = "@keyframes dlspin { to { transform: rotate(360deg); } }";
  document.head.appendChild(st);
}

if (
  typeof document !== "undefined" &&
  !document.getElementById("__dlscan_viewport__")
) {
  const st = document.createElement("style");
  st.id = "__dlscan_viewport__";
  st.textContent = `
    @supports (height: 100dvh) {
      .dlscan-modal { height: min(100dvh, 900px) !important; }
    }
    @supports not (height: 100dvh) {
      .dlscan-modal { height: min(100svh, 900px) !important; }
    }
  `;
  document.head.appendChild(st);
}
